import React from 'react';
import PropTypes from 'prop-types';
import logger from '../../utils/logger';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('ErrorBoundary caught an error', {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
    });

    this.setState({ error, errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-primary p-6">
          <div className="max-w-lg text-center p-10 bg-bg-secondary border border-red-500 rounded-xl shadow-lg">
            <h1 className="text-2xl font-semibold text-red-500 m-0 mb-3">Something went wrong</h1>
            <p className="text-base text-text-secondary m-0 mb-6 leading-relaxed">
              {this.props.showDetails && this.state.error
                ? this.state.error.toString()
                : 'We apologize for the inconvenience. Please try again.'}
            </p>
            
            {this.props.showDetails && this.state.errorInfo && (
              <details className="text-left p-3 bg-bg-input border border-border-primary rounded-md mb-6 max-h-40 overflow-y-auto">
                <summary className="cursor-pointer">Error Details</summary>
                <pre className="font-mono text-sm text-red-500 m-0 whitespace-pre-wrap break-words">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button onClick={this.handleReset} className="py-3 px-5 text-sm font-medium rounded-md cursor-pointer transition-all duration-fast bg-primary-600 text-white border border-primary-600 hover:bg-primary-500 hover:border-primary-500">
                Try Again
              </button>
              <button onClick={() => window.location.reload()} className="py-3 px-5 text-sm font-medium rounded-md cursor-pointer transition-all duration-fast bg-bg-tertiary text-text-primary border border-border-primary hover:bg-bg-hover hover:border-border-hover">
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  onReset: PropTypes.func,
  showDetails: PropTypes.bool,
};

ErrorBoundary.defaultProps = {
  showDetails: process.env.NODE_ENV === 'development',
};

export default ErrorBoundary;
